import React, { useEffect, useRef, useState, useMemo } from "react";
import { TitleBar } from "../../Components/Common/TitleBar/titleBar";
import {
  Button,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
  Table,
  Toast,
  ToastBody,
  ToastHeader,
} from "reactstrap";
import "./style.scss";

import { TripModal } from "../../Components/Common/TripModal/tripModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import { Pagination } from "../../Components/Common/Pagination";
import { useDispatch, useSelector } from "react-redux";

import {
  createDriver,
  deleteDriver,
  getAllDriverList,
  getDriverById,
  updateDriver,
} from "../../slices/driverManagement/thunk";
import { getAllRoleList } from "../../slices/rolesAndPermission/thunk";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  hideErrorMessage,
  setLoading,
} from "../../slices/driverManagement/reducer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import PhoneNumberField from "../../Components/Common/PhoneNumberField";
import { removeCountryCode } from "../../constant/utils";

const DriverManagement = () => {
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const driverList = useSelector(
    (state) => state.DriverManagementSlice.driverList
  );
  const driverById = useSelector(
    (state) => state.DriverManagementSlice.driverById
  );
  const loading = useSelector((state) => state.DriverManagementSlice.loading);
  const rolesList = useSelector((state) => state.Roles.rolesList);
  const [viewDetails, setViewDetails] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [deleteUserModal, setDeleteUserModal] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [toasterType, setToasterType] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [showToPicker, setShowToPicker] = useState(false);

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const formatDateTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const toggleViewDetailModal = () => {
    setViewDetails(!viewDetails);
  };

  const toggleAddUserModal = async () => {
    setAddUserModal(!addUserModal);
  };

  const toggleDeleteUserModal = async () => {
    setDeleteUserModal(!deleteUserModal);
  };

  const driverManagementSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email"),
    license_number: Yup.string().matches(
      /^[a-zA-Z0-9\- ]{5,20}$/,
      "License number must be 5–20 alphanumeric characters"
    ),
    shift_time_end: Yup.date().min(
      Yup.ref("shift_time_start"),
      "End time must be after start time"
    ),
  });

  useEffect(() => {
    try {
      dispatch(getAllRoleList("None", "None", ""));
    } catch (error) {}
  }, [dispatch]);

  useEffect(() => {
    try {
      dispatch(getAllRoleList("None", "None", ""));
    } catch (error) {}
    const delayDebounceFn = setTimeout(() => {
      try {
        dispatch(getAllDriverList(currentPage, pageSize, searchText));
      } catch (error) {}
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [dispatch, currentPage, pageSize, searchText]);

  const driverRoleId = useMemo(() => {
    if (!Array.isArray(rolesList)) return "";
    return rolesList.find((role) => role.name === "Driver")?.id || "";
  }, [rolesList]);

  const addInitialValues = {
    first_name: "",
    email: "",
    last_name: "",
    role: driverRoleId || "",
    license_number: "",
    working_hours: "",
    phone_number: "",
    country_code: "",
    shift_time_start: "",
    shift_time_end: "",
    status: "active",
  };

  const updateInitialValues = {
    first_name: driverById?.first_name || null,
    email: driverById?.email || null,
    last_name: driverById?.last_name || null,
    role: driverRoleId || null,
    license_number: driverById?.license_number || null,
    working_hours: driverById?.working_hours || null,
    phone_number: driverById?.phone_number ?? null,
    country_code: driverById?.country_code || null,
    shift_time_start: driverById?.shift_time_start
      ? new Date(driverById.shift_time_start)
      : null,
    shift_time_end: driverById?.shift_time_end
      ? new Date(driverById.shift_time_end)
      : null,
    status: driverById?.status,
  };

  const showToaster = (isSuccess, message) => {
    setShowToast(true);
    setToasterType(isSuccess ? "success" : "danger");
    setToastMessage(message);
    setTimeout(() => {
      setShowToast(false);
      if (!isSuccess) {
        dispatch(hideErrorMessage());
      }
    }, 3000);
  };

  const handleUsersSubmit = async (values) => {
    try {
      let response;
      if (!isAdd) {
        response = await dispatch(updateDriver(driverById.id, values));
      } else {
        response = await dispatch(createDriver(values));
      }
      const { success: isSuccess, message } = response;

      showToaster(isSuccess, message || "Operation successful");
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while processing your request"
      );
    } finally {
      toggleAddUserModal();
      await dispatch(getAllDriverList(currentPage, pageSize, searchText));
    }
  };
  const handleDeleteUser = async () => {
    try {
      const { success: isSuccess, message } = await dispatch(
        deleteDriver(driverById.id)
      );
      if (isSuccess) {
        showToaster(isSuccess, message || "Driver Successfully Deleted");
      }
    } catch (error) {
      showToaster(
        false,
        error?.message || "An error occurred while deleting the user"
      );
    } finally {
      S;
      toggleDeleteUserModal();
      await dispatch(getAllDriverList(currentPage, pageSize, searchText));
    }
  };

  document.title = "Driver Management | D&A";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-column gap-4">
            <TitleBar title={"Driver Management"} />

            <div
              className="position-fixed top-0 end-0 p-3"
              style={{ zIndex: 1055 }}
            >
              {showToast && (
                <Toast isOpen={showToast}>
                  <ToastHeader
                    icon={"danger" === toasterType ? "danger" : "success"}
                    toggle={() => setShowToast(false)}
                  >
                    {"danger" === toasterType ? "Error" : "Success"}
                  </ToastHeader>
                  <ToastBody>{toastMessage}</ToastBody>
                </Toast>
              )}
            </div>

            <div className="userManagementSection">
              <div className="d-flex justify-content-end align-items-end p-2 p-lg-0 gap-1 gap-lg-0">
                <InputGroup className="search-input">
                  <InputGroupText>
                    <i className="ri-search-line" />
                  </InputGroupText>
                  <Input
                    type="text"
                    placeholder="Search input text"
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        dispatch(getAllDriverList(1, pageSize, searchText));
                        setCurrentPage(1);
                      }
                    }}
                  />
                </InputGroup>
                <Button
                  onClick={() => {
                    setIsAdd(true);
                    toggleAddUserModal();
                    dispatch(setLoading({ type: "read", value: false }));
                  }}
                  className="newUserManagementBtn d-flex gap-2"
                >
                  <span className="d-none d-md-flex">Create Driver</span>
                  <i className="ri-add-line" />
                </Button>
              </div>

              <div className="trip-table">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Sr.No</th>
                      <th>Driver Name</th>
                      <th>License Number</th>
                      <th>Vehicle Number</th>
                      <th>Working Hours</th>
                      <th>Email </th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>

                  {loading.read ? (
                    <tbody>
                      <tr>
                        <td colSpan="9" className="text-center align-middle">
                          <Spinner color="primary" />
                        </td>
                      </tr>
                    </tbody>
                  ) : (
                    <tbody>
                      {driverList?.results?.length ? (
                        driverList?.results?.map((element, index) => (
                          <tr key={index}>
                            <td>{(currentPage - 1) * pageSize + index + 1}</td>
                            <td>
                              {element.first_name} {element.last_name}
                            </td>
                            <td>{element.license_number || "-"}</td>
                            <td>{element.VehicleNumber || "-"} </td>
                            <td>{element.working_hours || "-"}</td>
                            <td>{element.email || "-"}</td>
                            <td>
                              <span
                                style={{
                                  display: "inline-block",
                                  width: "80px",
                                  textAlign: "center",
                                  backgroundColor:
                                    element?.status === "active"
                                      ? "#28a74533"
                                      : element?.status === "assigned"
                                      ? "#ffc10733"
                                      : "#dc354533",
                                  padding: "4px 8px",
                                  borderRadius: "10px",
                                  color:
                                    element?.status === "active"
                                      ? "#28a745"
                                      : element?.status === "assigned"
                                      ? "#ffc107"
                                      : "#dc3545",
                                  fontWeight: 500,
                                }}
                              >
                                {element?.status === "active"
                                  ? "Active"
                                  : element?.status === "assigned"
                                  ? "Assigned"
                                  : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <div className="action-icons">
                                <i
                                  className="ri-eye-fill icon"
                                  style={{ color: "#E8A014" }}
                                  onClick={() => {
                                    dispatch(getDriverById(element.id));
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                    toggleViewDetailModal();
                                  }}
                                />
                                <i
                                  className="ri-pencil-fill icon"
                                  style={{ color: "#007AFF" }}
                                  onClick={() => {
                                    dispatch(getDriverById(element.id));
                                    setIsAdd(false);
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                    toggleAddUserModal();
                                  }}
                                />
                                <i
                                  className="ri-delete-bin-fill icon"
                                  style={{ color: "#FB0000" }}
                                  onClick={() => {
                                    dispatch(getDriverById(element.id));
                                    dispatch(
                                      setLoading({ type: "read", value: false })
                                    );
                                    toggleDeleteUserModal();
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="text-start text-sm-center" colSpan={9}>
                            <h3>No Driver Found</h3>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  )}
                </Table>
              </div>
              {driverList?.results?.length > 0 && (
                <Pagination
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  totalItems={driverList?.count || 0}
                />
              )}
            </div>
          </div>

          <TripModal
            isModalOpen={viewDetails}
            toggleModal={toggleViewDetailModal}
            buttonText="Assign"
            closeText={"Close without saving"}
            title={"Driver Management"}
            tripText={"Driver Details"}
            showButtons={false}
            showDeleteIcon={false}
            showEditIcon={false}
            showcloseIcon={true}
            editIconClick={() => {
              toggleViewDetailModal();
              toggleAddUserModal();
              setIsAdd(false);
            }}
            deleteIconClick={() => {
              toggleViewDetailModal();
              toggleDeleteUserModal();
            }}
          >
            <div className="px-3 d-flex flex-column gap-4">
              <Row className="mb-5">
                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Driver Name<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {driverById?.first_name} {driverById?.last_name}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Email<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {driverById?.email}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Phone Number<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {removeCountryCode(
                        driverById?.country_code,
                        driverById?.phone_number
                      )}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      License Number<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {" "}
                      {driverById?.license_number}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Vehicle Number<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {driverById?.VehicleNumber || "---"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Assigned Vehicle<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {driverById?.assigned_vehicle || "---"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Completed Trips<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {driverById?.completed_trips}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Shift Time<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue h-25">
                      {driverById?.shift_time_start &&
                      driverById?.shift_time_end
                        ? `${formatDateTime(
                            driverById.shift_time_start
                          )} - ${formatDateTime(driverById.shift_time_end)}`
                        : "---"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Working Hours<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue">
                      {driverById?.working_hours || "---"}
                    </span>
                  </div>
                </div>

                <div className="custom-box-value border-0 pb-0">
                  <div className="d-flex flex-column flex-md-row items-center justify-space-between gap-1 gap-md-3">
                    <span className="userDetailLabel">
                      Status<span className="d-md-none">:</span>
                    </span>
                    <span className="userDetailValue mt-0">
                      <span
                        style={{
                          display: "inline-block",
                          width: "80px",
                          textAlign: "center",
                          backgroundColor:
                            driverById?.status === "active"
                              ? "#28a74533"
                              : driverById?.status === "assigned"
                              ? "#ffc10733"
                              : "#dc354533",
                          padding: "4px 8px",
                          borderRadius: "10px",
                          color:
                            driverById?.status === "active"
                              ? "#28a745"
                              : driverById?.status === "assigned"
                              ? "#ffc107"
                              : "#dc3545",
                          fontWeight: 500,
                        }}
                      >
                        {driverById?.status === "active"
                          ? "Active"
                          : driverById?.status === "assigned"
                          ? "Assigned"
                          : "Inactive"}
                      </span>
                    </span>
                  </div>
                </div>
              </Row>
            </div>
          </TripModal>

          <TripModal
            isModalOpen={addUserModal}
            toggleModal={toggleAddUserModal}
            buttonText={isAdd ? "Add" : "Update"}
            closeText={"Close without saving"}
            title={"Driver Management"}
            tripText={isAdd ? "Add Driver New" : "Update Driver"}
            showcloseIcon={true}
            size="lg"
            loading={isAdd ? loading.create : loading.update}
            onSubmit={() => {
              if (formikRef.current) {
                formikRef.current.handleSubmit();
              }
            }}
          >
            <div className="px-3 d-flex gap-4">
              <Formik
                innerRef={formikRef}
                enableReinitialize
                initialValues={isAdd ? addInitialValues : updateInitialValues}
                validationSchema={driverManagementSchema}
                onSubmit={handleUsersSubmit}
                validateOnChange={true}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="first_name" className="passwordLabel">
                            First Name
                          </Label>
                          <Input
                            type="text"
                            name="first_name"
                            className="profileInput"
                            id="first_name"
                            placeholder="Enter First Name"
                            value={values.first_name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="last_name" className="passwordLabel">
                            Last Name
                          </Label>
                          <Input
                            type="text"
                            name="last_name"
                            className="profileInput"
                            id="last_name"
                            placeholder="Enter Last Name"
                            value={values.last_name}
                            onBlur={handleBlur}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="email" className="passwordLabel">
                            Email
                          </Label>
                          <Input
                            type="text"
                            name="email"
                            className="profileInput"
                            id="email"
                            placeholder="Enter Email"
                            value={values.email}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={errors.email ? true : false}
                          />
                          {errors.email ? (
                            <FormFeedback type="invalid">
                              {errors.email}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <PhoneNumberField
                          label="Phone Number"
                          phoneValue={values.phone_number}
                          countryCode={updateInitialValues.country_code}
                          onChange={(phone, code) => {
                            formikRef.current.setFieldValue(
                              "phone_number",
                              phone
                            );
                            formikRef.current.setFieldValue(
                              "country_code",
                              code
                            );
                          }}
                          onBlur={handleBlur}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="license_number" className="passwordLabel">
                            License Number
                          </Label>
                          <Input
                            type="text"
                            name="license_number"
                            className="profileInput"
                            id="license_number"
                            placeholder="Enter License Number"
                            value={values.license_number}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={errors.license_number ? true : false}
                          />
                          {errors.license_number ? (
                            <FormFeedback type="invalid">
                              {errors.license_number}
                            </FormFeedback>
                          ) : null}
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label for="working_hours" className="passwordLabel">
                            Working Hours
                          </Label>
                          <Input
                            type="number"
                            name="working_hours"
                            className="profileInput"
                            id="working_hours"
                            placeholder="Enter Working Hours"
                            value={values.working_hours}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            invalid={
                              touched.working_hours && errors.working_hours
                                ? true
                                : false
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup className="position-relative" ref={fromRef}>
                          <Label
                            for="shift_time_start"
                            className="passwordLabel"
                          >
                            Start Shift Time
                          </Label>
                          <Input
                            type="text"
                            id="shift_time_start"
                            placeholder="Select Start Shift Time"
                            value={formatDateTime(values.shift_time_start)}
                            readOnly
                            className="profileInput"
                            onClick={() => setShowFromPicker((prev) => !prev)}
                          />
                          {showFromPicker && (
                            <div className="driverdatetimepicker">
                              <DatePicker
                                selected={values.shift_time_start}
                                value={values.shift_time_start}
                                onChange={(date) => {
                                  setShowFromPicker(false);
                                  formikRef.current.setFieldValue(
                                    "shift_time_start",
                                    date
                                  );
                                }}
                                showTimeSelect
                                inline
                                dateFormat="MMM dd, yyyy hh:mm aa"
                              />
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup className="position-relative" ref={toRef}>
                          <Label for="shift_time_end" className="passwordLabel">
                            End Shift Time
                          </Label>
                          <Input
                            type="text"
                            id="shift_time_end"
                            placeholder="Select End Shift Time"
                            value={formatDateTime(values.shift_time_end)}
                            readOnly
                            className="profileInput"
                            onClick={() => setShowToPicker((prev) => !prev)}
                          />
                          {showToPicker && (
                            <div className="driverdatetimepicker">
                              <DatePicker
                                selected={values.shift_time_end}
                                value={values.shift_time_end}
                                onChange={(date) => {
                                  setShowToPicker(false);
                                  formikRef.current.setFieldValue(
                                    "shift_time_end",
                                    date
                                  );
                                }}
                                showTimeSelect
                                inline
                                dateFormat="MMM dd, yyyy hh:mm aa"
                              />
                            </div>
                          )}
                          {touched.shift_time_end && errors.shift_time_end && (
                            <div className="invalid-feedback d-block">
                              {errors.shift_time_end}
                            </div>
                          )}
                          {touched.shift_time_end && errors.shift_time_end && (
                            <div className="invalid-feedback d-block">
                              {errors.shift_time_end}
                            </div>
                          )}
                          {touched.shift_time_end && errors.shift_time_end && (
                            <div className="invalid-feedback d-block">
                              {errors.shift_time_end}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label for="status" className="passwordLabel">
                            Status
                          </Label>
                          <Input
                            type="select"
                            name="status"
                            className="profileInput"
                            id="status"
                            value={values.status}
                            onChange={handleChange}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="assign">Assign</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </div>
          </TripModal>

          <DeleteModal
            show={deleteUserModal}
            loading={loading.delete}
            onDeleteClick={() => {
              handleDeleteUser();
            }}
            onCloseClick={toggleDeleteUserModal}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DriverManagement;
